import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  lighten,
  OutlinedInput,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { useFieldContext } from "components/Forms/AppForm";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useSearchIntercept } from "hooks/useSearchIntercept";

interface SearchBarProps {
  debounceThreshold?: number;
  onChange?: (_searchTerm: string) => void;
  onSearch?: (_searchTerm: string) => void;
  captureSearch?: boolean;
}

const SearchBar = ({
  onChange,
  onSearch,
  debounceThreshold = 150, // default to 150ms adjust as needed
  captureSearch = false,
}: SearchBarProps) => {
  const field = useFieldContext<string>();
  const [search, setSearch] = useState(field.state.value || "");
  const debouncedSearchTerm = useDebouncedValue(search, debounceThreshold);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    field.handleChange(debouncedSearchTerm);
    onChange && onChange(debouncedSearchTerm);
  }, [debouncedSearchTerm, onChange, field]);

  const clearSearch = () => {
    setSearch("");
    field.setValue("");
  };

  useSearchIntercept({
    onSearch: () => {
      if (!captureSearch) return;
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300); // there is a slide in animation that needs to complete before we can focus
    },
    disableNative: true,
  });

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel htmlFor="search-input">Keyword Search</InputLabel>
      <OutlinedInput
        inputRef={inputRef}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        type="text"
        fullWidth
        id="search-input"
        label="Keyword Search"
        sx={{ overflow: "hidden", borderRadius: "6px" }}
        endAdornment={
          <InputAdornment
            position="end"
            sx={{
              margin: 0,
              marginRight: "-1px",
              maxHeight: "unset",
            }}
          >
            {search.length > 0 && (
              <IconButton
                size="small"
                onClick={clearSearch}
                sx={{ marginRight: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}

            <IconButton
              edge="end"
              sx={{
                color: "white",
                backgroundColor: "var(--bs-dark)",
                borderRadius: 0,
                "&:hover": {
                  backgroundColor: lighten("#212529", 0.15),
                },
                "& .MuiTouchRipple-child": {
                  transform: "scale(1.5)",
                },
              }}
              onClick={() => onSearch && onSearch(field.state.value)}
            >
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );
};
export default SearchBar;
